import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { useFrame } from '@react-three/fiber';
import { Frustum, Matrix4, Vector3 } from 'three';

import { getActiveCamera } from '../reactThreeFiberBindings';

const _m = new Matrix4(); // don't touch outside `CameraPositionAndFrustumTracker`

const context = createContext({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  subscribe: (callback: CameraFrameCallback) => () => {},
});

type CameraFrameCallback = (frustum: Frustum, position: Vector3) => void;

/**
 * Similar to the `useFrame` hook, but provides easy access to the current camera's frustum and position, and scales
 *   better with larger number of subscribers (`useFrame` sorted subscribers by priority, which can be expensive).
 *
 * This effect will never force a re-render. You should do that yourself if you need to, or ideally, react to the
 *   frustum in a more performance friendly way.
 *
 * Please don't mutate the frustum or position. Bad things might happen. :(
 */
export function useCameraFrame(callback: (frustum: Frustum, position: Vector3) => void) {
  const subscribe = useContext(context).subscribe;
  useLayoutEffect(() => subscribe(callback));
}

/**
 * Ensure this component is rendered ahead of anything that needs the frustum.
 */
export function CameraPositionAndFrustumTracker({ children }: PropsWithChildren) {
  const [cameraFrustum] = useState(() => new Frustum());
  const [cameraPosition] = useState(() => new Vector3());

  const [subscriptions] = useState(() => [] as CameraFrameCallback[]);

  const subscribe = useCallback(
    (callback: CameraFrameCallback) => {
      subscriptions.push(callback);

      return () => {
        const index = subscriptions.indexOf(callback);
        if (index !== -1) {
          subscriptions.splice(index, 1);
        }
      };
    },
    [subscriptions],
  );

  const contextValue = useMemo(() => ({ subscribe }), [subscribe]);

  useFrame(() => {
    const camera = getActiveCamera();

    if (camera) {
      camera.getWorldPosition(cameraPosition);
      cameraFrustum.setFromProjectionMatrix(_m.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse));
    } else {
      return;
    }

    for (const subscription of subscriptions) {
      subscription(cameraFrustum, cameraPosition);
    }
  });

  return <context.Provider value={contextValue}>{children}</context.Provider>;
}
